import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal, ModalProps } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useLocationService } from '@/services/location/LocationService';
import { useTemplatesForLocation } from '@/services/shared/useTemplatesForLocation';
import { strings } from '@/services/translation/strings';
import { WorkingRoster } from '@/types/roster.types';
import { format } from 'date-fns';
import { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateTemplateFromUpdateTemplate } from '../hooks/useCreateTemplateFromUpdateTemplate';
import { useGetUpdateTemplateFromWorkingRoster } from '../hooks/useGetUpdateTemplateFromWorkingRoster';

interface AddTemplateFormValues {
  name: string;
}

interface CreateTemplateFromRosterModalProps extends ModalProps {
  workingRoster: WorkingRoster;
}

const CreateTemplateFromRosterModal: FunctionComponent<CreateTemplateFromRosterModalProps> = ({
  open,
  onClose,
  workingRoster
}) => {
  const { date } = useParams<{ date: string }>();

  if (!date) throw new Error('Missing date');

  const formattedDate = format(new Date(date), 'EEEE do MMMM yyyy');

  const methods = useForm<AddTemplateFormValues>({
    defaultValues: {
      name: `Copy of ${formattedDate}`
    }
  });

  const getUpdateTemplate = useGetUpdateTemplateFromWorkingRoster();

  const selectedLocation = useLocationService(s => s.selectedLocation);

  const { templateOptions, refetch } = useTemplatesForLocation();

  const navigate = useNavigate();
  const { createTemplate, fetching } = useCreateTemplateFromUpdateTemplate();

  const onSubmit = async (values: AddTemplateFormValues) => {
    const updateTemplate = getUpdateTemplate(workingRoster);
    const workingRosterTemplate = templateOptions.find(template => template.name === workingRoster.templateName);

    updateTemplate.name = values.name;
    updateTemplate.locationId = selectedLocation?.id;
    updateTemplate.targetType = workingRosterTemplate?.template.target?.type || '';
    updateTemplate.targetValue = workingRosterTemplate?.template.target?.value || 0;
    updateTemplate.weekday = !!workingRosterTemplate?.template.weekday;

    const result = await createTemplate(updateTemplate);

    await refetch({ requestPolicy: 'network-only' });

    setTimeout(
      () =>
        handleMutationResult(result, {
          successMessage: strings.common.success,
          errorMessage: strings.common.error,
          customSuccessCheck: data => !!data?.updateTemplate,
          onSuccess: data => {
            onClose();
            navigate(`/templates/${data?.updateTemplate?.id}`);
          }
        }),
      100
    );
  };

  return (
    <Modal open={open} onClose={onClose} header={`Create New Template From Roster: ${formattedDate}`}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={'Template Name'}
              name="name"
              required
              rules={{
                validate: (value: string) => {
                  const isDuplicate = templateOptions?.some(t => t?.name === value);
                  if (isDuplicate) return 'Template name must be unique';
                  return true;
                }
              }}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={onClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default CreateTemplateFromRosterModal;
